"use client";

import {
	Button,
	type DefaultImageProps,
	type DefaultLinkProps,
	Heading,
	type HeadingProps,
	Icon,
	type IconProps,
	Image,
	type ImageProps,
	Link,
	type LinkProps,
	Modal,
	RichText,
	Text
} from "@pamperedchef/design-system";

import * as styles from "./ConsumableProductDetail.module.scss";

interface ConsumableProductDetailProps<
	LinkComponentProps = DefaultLinkProps,
	ImageComponentProps = DefaultImageProps
> {
	heading: HeadingProps;
	description: string;
	subheadingAbout: HeadingProps;
	allergenIngredients: {
		icon: IconProps;
		label: string;
	}[];
	subheadingDetails: HeadingProps;
	details: string;
	cta?: {
		label: string;
		icon_name: IconProps["name"];
	};
	rteContent?: string;
	rteImage?: ImageProps<ImageComponentProps>;
	rteCta?: LinkProps<LinkComponentProps>;
}

/**
 * Consumable Product Detail component
 */
export function ConsumableProductDetail<
	LinkComponentProps = DefaultLinkProps,
	ImageComponentProps = DefaultImageProps
>({
	heading,
	description,
	subheadingAbout,
	allergenIngredients,
	subheadingDetails,
	details,
	cta,
	rteContent,
	rteImage,
	rteCta
}: ConsumableProductDetailProps<
	LinkComponentProps,
	ImageComponentProps
>): JSX.Element {
	return (
		<div className={styles["consumable-product-detail"]}>
			<Heading {...heading} className={styles["heading"]} styledAs="h1" />
			<RichText content={description} className={styles["description"]} />
			<Heading
				{...subheadingAbout}
				className={styles["subheading"]}
				styledAs="h2"
			/>
			<div className={styles["allergen-ingredients"]}>
				{allergenIngredients.map((allergenIngredient, index) => (
					<span key={index} className={styles["allergen-ingredient"]}>
						<Icon
							{...allergenIngredient.icon}
							className={styles["icon"]}
							size={24}
						/>
						<Text styledAs="xsr" className="mb-0">
							{allergenIngredient.label}
						</Text>
					</span>
				))}
			</div>
			{cta && rteContent && (
				<Modal
					triggerElement={
						<Button
							icon={{ position: "left", name: cta.icon_name }}
							size="lg"
							kind="secondary"
							className={styles["cta"]}
						>
							{cta.label}
						</Button>
					}
				>
					<div className={styles["rte-content"]}>
						<RichText content={rteContent} />
						{rteImage && (
							<Image
								{...(rteImage as ImageProps<ImageComponentProps>)}
								alt={
									rteImage.alt ||
									"Consumable Product Detail Image"
								}
							/>
						)}
						{rteCta && <Link {...rteCta} />}
					</div>
				</Modal>
			)}
			<Heading
				{...subheadingDetails}
				className={styles["subheading"]}
				styledAs="h2"
			/>
			<RichText content={details} className={styles["details"]} />
		</div>
	);
}
